import React, { useState } from 'react';
import { Box, Typography, TextField, Button, MenuItem, Alert } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Navbar from './Navbar';
import Footer from './Footer';
import SectionContainer from './SectionContainer';
import SectionEyebrow from './SectionEyebrow';
import SEO from '../SEO/SEO';

const topics = ['General question', 'Billing & payments', 'Bug report', 'Feature request', 'Something else'];

const initialForm = { name: '', email: '', topic: topics[0], message: '' };

const infoCards = [
  {
    Icon: EmailOutlinedIcon,
    title: 'Email us',
    description: 'For anything account or billing related.',
    detail: 'support@arrayscript.com',
  },
  {
    Icon: ChatBubbleOutlineIcon,
    title: 'Live chat',
    description: "We're usually online during business hours.",
    detail: 'Mon – Fri, 9am – 6pm',
  },
  {
    Icon: HelpOutlineIcon,
    title: 'FAQs',
    description: 'Check if your question is already answered.',
    detail: 'Visit the FAQ section',
  },
];

const ContactPage = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = 'Please enter your name';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) nextErrors.email = 'Please enter a valid email';
    if (!form.message.trim()) nextErrors.message = 'Please tell us how we can help';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitted(true);
    setForm(initialForm);
  };

  return (
    <Box sx={{ bgcolor: 'background.default', color: 'text.primary', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SEO
        title="Contact Us"
        description="Get in touch with the Remembr team at Arrayscript for support, billing questions, bug reports or feature requests."
        path="/contact"
      />
      <Navbar />

      <SectionContainer sx={{ py: { xs: 6, md: 10 }, flex: 1 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: { xs: 5, md: 7 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <SectionEyebrow>Help Center</SectionEyebrow>
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: 26, md: 36 }, mb: 1.5 }}>
            We're here to help
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: { xs: 14, md: 15.5 } }}>
            Have a question, ran into an issue, or just want to say hello? Send us a message and our team will
            get back to you shortly.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 4, md: 6 }, alignItems: 'flex-start' }}>
          <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: 300 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {infoCards.map(({ Icon, title, description, detail }) => (
              <Box
                key={title}
                sx={(theme) => ({
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
                })}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(37,99,235,0.15)' : '#E9F0FF'),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.5,
                  }}
                >
                  <Icon fontSize="small" />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 0.5 }}>{title}</Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: 13.5, mb: 0.75 }}>{description}</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: 'primary.main' }}>{detail}</Typography>
              </Box>
            ))}
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={(theme) => ({
              flex: 1,
              width: '100%',
              p: { xs: 3, sm: 4.5 },
              borderRadius: 4,
              border: '1px solid',
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fff',
            })}
          >
            {submitted && (
              <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSubmitted(false)}>
                Thanks for reaching out. We've received your message and will reply within 1-2 business days.
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2.5, mb: 2.5 }}>
              <TextField
                label="Full name"
                fullWidth
                value={form.name}
                onChange={handleChange('name')}
                error={Boolean(errors.name)}
                helperText={errors.name}
              />
              <TextField
                label="Email address"
                type="email"
                fullWidth
                value={form.email}
                onChange={handleChange('email')}
                error={Boolean(errors.email)}
                helperText={errors.email}
              />
            </Box>

            <TextField
              select
              label="What's this about?"
              fullWidth
              value={form.topic}
              onChange={handleChange('topic')}
              sx={{ mb: 2.5 }}
            >
              {topics.map((topic) => (
                <MenuItem key={topic} value={topic}>
                  {topic}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Message"
              fullWidth
              multiline
              minRows={5}
              value={form.message}
              onChange={handleChange('message')}
              error={Boolean(errors.message)}
              helperText={errors.message}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              variant="contained"
              endIcon={<SendIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', borderRadius: 999, px: 3.5, py: 1.1, fontWeight: 600, boxShadow: 'none' }}
            >
              Send message
            </Button>
          </Box>
        </Box>
      </SectionContainer>

      <Footer />
    </Box>
  );
};

export default ContactPage;
